import { ReactNode, useCallback } from 'react';
import { Button, EXTRA_SMALL, TERTIARY } from '@glu/buttons-react';
import { XCircleAshIcon } from '@glu/icons-react';
import { Box } from '@glu/layout-react';
import locale from '@glu/locale';
import { useTheme } from '@glu/theming';
import { Typography } from '@glu/typography-react';
import useStyles from './Pill.styles';

interface IPillProps {
  /** For custom testing purposes. Will override data-qa attr for the root pill container */
  dataQa?: string;
  /** What will be rendered within the pill. */
  children: ReactNode;
  /** Unique Id passed back with the onRemove click. */
  id: string;
  /** Callback when remove icon is clicked. */
  onRemove(id: IPillProps['id']): void;
  hideRemoveIcon?: undefined;
}

interface INonRemovablePillProps {
  dataQa?: IPillProps['dataQa'];
  children: IPillProps['children'];
  id?: never;
  onRemove?: never;
  /** Hides the remove icon. */
  hideRemoveIcon: true;
}

export type TPillProps = INonRemovablePillProps | IPillProps;

const Pill = ({ children, dataQa, hideRemoveIcon, id, onRemove }: TPillProps) => {
  const classes = useStyles();
  const { spacing } = useTheme();
  const handleRemove = useCallback(() => onRemove!(id!), [id, onRemove]);
  return (
    <div className={classes.root} data-qa={dataQa}>
      {children}
      {!hideRemoveIcon && (
        <Box
          sx={{
            display: 'flex',
            paddingLeft: spacing.unit,
          }}
        >
          <Button
            className={classes.removeButton}
            onClick={handleRemove}
            size={EXTRA_SMALL}
            variant={TERTIARY}
          >
            <XCircleAshIcon noIconWrapper />
            <Typography variant="accessibility">{locale.get('remove')}</Typography>
          </Button>
        </Box>
      )}
    </div>
  );
};

export default Pill;
